"use client";

import { BrandNames } from "@hopper-b2b/types";
import {
  DateTimeFormatStyle,
  formatDateTime as hopperFormatDateTime,
} from "@hopper-i18n/formatter";
import { useCallback } from "react";
import { useI18nextContext } from "../I18nContext";
import { setLang } from "../utils/getLang";
import { useI18nCurrency } from "./useI18nCurrency";
import { i18n } from "i18next";

export { DateTimeFormatStyle };

export interface I18nContextType extends i18n {
  brand?: BrandNames;
  formatZeroPrice: (currency?: string) => string | undefined;
  formatDateTime: (date: Date, style: DateTimeFormatStyle) => string;
}

export function useI18nContext(): I18nContextType &
  ReturnType<typeof useI18nCurrency> {
  const ctx = useI18nextContext();
  if (ctx === undefined) throw new Error(`must be used within a I18nProvider`);
  const {
    language,
    options: { resources, fallbackLng },
  } = ctx;

  let brand = resources?.[language]?.brand as BrandNames | undefined;

  const supported =
    brand?.supportedLanguages?.some((l) => l.key === language) === true;
  if (!supported) {
    // Use the i18n fallback if tenant does not support this language
    let fallback = "en";
    if (typeof fallbackLng === "string") {
      fallback = fallbackLng;
    } else if (Array.isArray(fallbackLng)) {
      const firstLang: unknown = fallbackLng[0];
      if (typeof firstLang === "string") {
        fallback = firstLang;
      }
    }
    const brandEntry: unknown = resources?.[fallback]?.brand;
    brand = brandEntry as BrandNames | undefined;
    setLang(fallback);
  }

  const formatDateTime = useCallback(
    (date: Date, style: DateTimeFormatStyle) =>
      hopperFormatDateTime(date, language, style),
    [language]
  );

  const {
    currencyCode,
    currencyNameOf,
    currencySymbol,
    formatFiatCurrencyForShopping,
    formatFiatCurrency,
    formatZeroAmount,
  } = useI18nCurrency();

  const formatZeroPrice = useCallback(
    (currency?: string) => {
      return formatFiatCurrency(
        {
          value: 0,
          currencyCode: currency ?? currencyCode,
        },
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      );
    },
    [currencyCode, formatFiatCurrency]
  );

  return {
    ...ctx,
    brand,
    currencyCode,
    currencyNameOf,
    currencySymbol,
    formatFiatCurrencyForShopping,
    formatFiatCurrency,
    formatZeroAmount,
    formatZeroPrice,
    formatDateTime,
  } as I18nContextType & ReturnType<typeof useI18nCurrency>;
}
